import { useQuery } from "@apollo/client/react";
import { Suspense, useCallback, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import DialingScreen, { BlankDialingScreen } from "../Call/DialingScreen";
import TwilioLiveCall from "../Call/LiveCall/TwilioLiveCall";
import getFrom from "../lib/getFrom";
import { meetingSecurityFeatureToCallFeature } from "../lib/securityFeaturesUtils.ts";
import StreamTrackerProvider from "../lib/StreamTrackerProvider";
import { useCurrentCorrespondent } from "../SessionBoundary.tsx";
import { GetApprovedPhoneDocument } from "./GetApprovedPhone.generated";

type VoiceCallPhase =
  | "dialing"
  | "call"
  | "ended"
  | "terminated"
  | "missed"
  | "missed-closed"
  | "connectedElsewhere";

export default function TwilioCallScreen() {
  const navigate = useNavigate();
  const { phoneId } = useParams<{ phoneId: string }>();
  const location = useLocation();
  const from = getFrom(location) || "/";
  const [phase, setPhase] = useState<VoiceCallPhase>("call");
  const [muted, setMuted] = useState(false);
  const [alerts, setAlerts] = useState(true);

  const currentUser = useCurrentCorrespondent();
  const currentParticipant = {
    externalId: currentUser.id,
    fullName: currentUser.fullName,
  };

  if (!phoneId) throw new Error("Missing id");

  const onLeave = useCallback(() => navigate(from), [from, navigate]);

  const { data, loading, error } = useQuery(GetApprovedPhoneDocument, {
    fetchPolicy: "no-cache", // do not cache access token
    variables: {
      phoneId,
    },
  });

  if (error) throw error;

  if (!data || loading) return <BlankDialingScreen />;

  const jwt = data.twilioToken;
  const { name: contactName, number, security } = data.facilityApprovedPhone;
  const fakeDate = new Date().getTime();
  const features = security.map((r) => meetingSecurityFeatureToCallFeature[r]);

  let children: React.ReactNode;
  if (phase === "dialing") {
    children = (
      <DialingScreen
        currentParticipant={currentParticipant}
        muted={muted}
        alerts={alerts}
        onLeave={onLeave}
        onToggleAlerts={() => setAlerts((a) => !a)}
        onToggleAudio={() => setMuted((m) => !m)}
        call={{
          features,
          participants: [
            {
              externalId: "twilio-ad-hoc-correspondent",
              fullName: number,
            },
          ],
          startedAt: fakeDate,
          endedAt: fakeDate,
          unlimited: true,
        }}
        missed={false}
      />
    );
  } else if (phase === "call") {
    children = (
      <Suspense
        fallback={
          <DialingScreen
            currentParticipant={currentParticipant}
            muted={muted}
            alerts={alerts}
            onLeave={onLeave}
            onToggleAlerts={() => setAlerts((a) => !a)}
            onToggleAudio={() => setMuted((m) => !m)}
            call={{
              features,
              participants: [
                {
                  externalId: "twilio-ad-hoc-correspondent",
                  fullName: number,
                },
              ],
              startedAt: fakeDate,
              endedAt: fakeDate,
              unlimited: true,
            }}
            missed={false}
          />
        }
      >
        <TwilioLiveCall
          currentParticipant={currentParticipant}
          call={{
            phoneId,
            jwt,
            number,
            contactName,
            features,
          }}
          onLeave={onLeave}
          beginMuted={muted}
          alerts={alerts}
          onToggleAlerts={() => setAlerts((a) => !a)}
          onPeerJoined={() => undefined}
          onTerminated={() => setPhase("terminated")}
          onEnded={onLeave}
        />
      </Suspense>
    );
  } else {
    return <Navigate to={from} />;
  }

  return <StreamTrackerProvider>{children}</StreamTrackerProvider>;
}
